import type { NavigateFunction } from 'react-router-dom';
import type { OrgCapability, ServiceProvider, Vendor } from '@shopgnd/shared';
import { logAdminAction } from '@/api/admin';
import { deriveCapabilitiesFromVendor } from '@/lib/orgCapabilities';
import { hubQueryClient } from '@/lib/hubQueryClient';
import {
  useAdminImpersonationStore,
  type AdminImpersonationTarget,
} from '@/stores/adminImpersonationStore';
import type { AuthUser } from '@/stores/authStore';

/** Reactive impersonation state for React components (prefer over getState helpers). */
export function useAdminImpersonation(): {
  session: AdminImpersonationTarget | null;
  impersonating: boolean;
} {
  const session = useAdminImpersonationStore((s) => s.session);
  return { session, impersonating: session != null };
}

/** Hub access while impersonation profile is still loading (avoids /hub/admin ↔ /hub/home loops). */
export function impersonationHubFlags(session: AdminImpersonationTarget | null): {
  hasVendorHub: boolean;
  hasProHub: boolean;
} {
  if (!session) return { hasVendorHub: false, hasProHub: false };
  if (session.kind === 'vendor') return { hasVendorHub: true, hasProHub: false };
  return { hasVendorHub: false, hasProHub: true };
}

/** Workspace nav capabilities during admin account view. */
export function impersonationCapabilities(
  session: AdminImpersonationTarget | null,
  vendor: Vendor | null | undefined,
  pro: ServiceProvider | null | undefined,
): OrgCapability[] {
  if (!session) return [];
  if (session.kind === 'vendor') {
    if (vendor) return deriveCapabilitiesFromVendor(vendor);
    return ['shop', 'classifieds', 'property', 'payouts', 'bookings'];
  }
  if (pro || vendor) {
    const caps = new Set<OrgCapability>(['bookings', 'classifieds']);
    if (vendor) deriveCapabilitiesFromVendor(vendor).forEach((c) => caps.add(c));
    return [...caps];
  }
  return ['bookings', 'classifieds'];
}

export function canAdminImpersonate(user: AuthUser | null): boolean {
  if (!user) return false;
  const roles = user.roles ?? [];
  return roles.includes('admin') || roles.includes('platform_admin');
}

export function isAdminImpersonating(): boolean {
  return useAdminImpersonationStore.getState().session != null;
}

export function adminImpersonationSession(): AdminImpersonationTarget | null {
  return useAdminImpersonationStore.getState().session;
}

function invalidateHubCaches() {
  void hubQueryClient.invalidateQueries({ queryKey: ['hub'] });
}

export function startVendorImpersonation(
  vendor: Pick<Vendor, 'id' | 'businessName'>,
  user?: AuthUser | null,
) {
  const actor = user ?? null;
  if (!canAdminImpersonate(actor)) return;
  useAdminImpersonationStore.getState().setSession({
    kind: 'vendor',
    vendorId: vendor.id,
    label: vendor.businessName,
  });
  if (actor?.id) {
    logAdminAction(actor.id, 'impersonate_start', 'vendor', vendor.id, vendor.businessName);
  }
  invalidateHubCaches();
}

export function startProImpersonation(
  pro: Pick<ServiceProvider, 'id' | 'headline'>,
  user?: AuthUser | null,
) {
  const actor = user ?? null;
  if (!canAdminImpersonate(actor)) return;
  useAdminImpersonationStore.getState().setSession({
    kind: 'pro',
    proId: pro.id,
    label: pro.headline,
  });
  if (actor?.id) {
    logAdminAction(actor.id, 'impersonate_start', 'service_provider', pro.id, pro.headline);
  }
  invalidateHubCaches();
}

export function stopAdminImpersonation(user?: AuthUser | null) {
  const session = useAdminImpersonationStore.getState().session;
  useAdminImpersonationStore.getState().clearSession();
  const actor = user ?? null;
  if (actor?.id && session) {
    const targetType = session.kind === 'vendor' ? 'vendor' : 'service_provider';
    const targetId = session.kind === 'vendor' ? session.vendorId : session.proId;
    logAdminAction(actor.id, 'impersonate_stop', targetType, targetId, session.label);
  }
  invalidateHubCaches();
}

/** End account view and return to platform admin hub. */
export function stopAdminImpersonationAndReturn(
  navigate: NavigateFunction,
  user?: AuthUser | null,
  returnPath = '/hub/admin',
) {
  stopAdminImpersonation(user);
  navigate(returnPath, { replace: true });
}

/** Hub path to open after starting impersonation. */
export function hubPathForImpersonation(session: AdminImpersonationTarget): string {
  return session.kind === 'vendor' ? '/hub/home' : '/hub/pro';
}
